<script setup>
import SectionHero from '@/components/SectionHero.vue';
import SectionTextWithFlatCards from '@/components/SectionHalfText.vue';
import FlatCard from '@/components/FlatCard.vue';

import {useRouting} from "@/composables/routing.js";
const {href,url} = useRouting();

import { Head } from '@inertiajs/vue3'
import { Link } from '@inertiajs/vue3';
</script>

<template>
    <Head>
        <title>Hosting solutions for all your needs</title>
        <meta name="description" content="Whether website, online store, agency projects or SaaS applications - our managed cloud offers the right solution.">
        <link rel="canonical" :href="url('/solutions', 'en')">
        <link rel="alternate" hreflang="de" :href="url('/solutions', 'de')">
        <link rel="alternate" hreflang="en" :href="url('/solutions', 'en')">
    </Head>
    <div>
        <section-hero
            title="Solutions to meet your requirements"
            subtitle="
High-performance hosting solutions are crucial for the success of modern web applications. Whether website, online store, agency projects or SaaS applications - our managed cloud offers the perfect combination of performance, security and flexibility."
        >
        </section-hero>

        <section-text-with-flat-cards class="mt-10"
                                      id="website-hosting"
                                      title="Website hosting in the cloud"
        >
            <template #default>
                <p class="text-lg mt-10">
                    Our website hosting solutions cater to every need. Whether your site is static or
                    dynamic, built with WordPress, Joomla, Drupal, or a custom application, we provide
                    the perfect platform for your website.
                </p>
                <p class="text-lg mt-2">
                    Planning a major marketing campaign or product launch? Easily scale performance up
                    with just a few clicks - and back down when needed. This ensures you're
                    perfectly prepared for any surge in demand.
                </p>
                <p class="text-lg mt-2">
                    No need to worry about skyrocketing cloud costs. Our straightforward, transparent
                    pricing model is exceptionally cost-effective compared to other providers.
                </p>
            </template>
            <template #extra>
                <flat-card title="Simple configuration"
                           text="In our managed cloud, you can do everything conveniently with just a few clicks."
                />
                <flat-card title="Scale at any time"
                           text="Scale the performance of your website up or down at any time."
                />
                <flat-card title="For all systems"
                           text="Wordpress, Joomla, Drupal or your own application - we support all systems."
                />
                <flat-card title="Affordable & flexible"
                           text="We offer a low price and full flexibility from the very first website."
                />
            </template>
        </section-text-with-flat-cards>

        <section-text-with-flat-cards class="mt-32 lg:mt-56"
                                      id="shop-hosting"
                                      text-right
                                      title="Reliable hosting for your online store"
        >
            <template #default>
                <p class="text-lg mt-10">
                    If your online store is not fully accessible, it could result in significant revenue losses.
                    With our cloud-based store hosting, you can rest assured. We provide a high-performance
                    platform tailored for business-critical web hosting.
                </p>
                <p class="text-lg mt-2">
                    Maximum availability and guaranteed performance are crucial for running an online store.
                    Additionally, there are often specific technical requirements that traditional web hosting
                    cannot meet.
                </p>
            </template>
            <template #extra>
                <flat-card title="Highest availability"
                           text="Established technologies and redundant systems guarantee reliability at the highest level."
                />
                <flat-card title="Guaranteed performance"
                           text="Booked resources (RAM & CPU) are exclusively available to you at all times."
                />
                <flat-card title="Individually customizable"
                           text="Implement even complex application landscapes in our managed cloud"
                />
                <flat-card title="Fully managed"
                           text="Easy to use with no infrastructure or server management effort - everything runs in the managed cloud."
                />
            </template>
        </section-text-with-flat-cards>

        <section-text-with-flat-cards class="mt-32 lg:mt-56"
                                      id="agency-hosting"
                                      title="Hosting for agency projects"
        >
            <template #default>
                <p class="text-lg mt-10">
                    As an agency, you need a hosting solution that adapts seamlessly to your requirements.
                    Our managed cloud delivers exactly that. From small projects to large campaigns, we
                    provide the perfect solution for your needs.
                </p>
                <p class="text-lg mt-2">
                    With our cloud platform, you can focus entirely on your projects without worrying about
                    the infrastructure — we handle that for you. The platform’s exceptional flexibility
                    enables you to fulfill your clients’ needs quickly and effortlessly.
                </p>
            </template>
            <template #extra>
                <flat-card title="Flexibly scalable"
                           text="Adjust the performance of your projects at any time - upwards or downwards."
                />
                <flat-card title="Simple management"
                           text="With our intuitive interface, you always have your projects under control."
                />
                <flat-card title="Fast deployment"
                           text="Start your projects in just a few minutes - without long waiting times."
                />
                <flat-card title="Full control"
                           text="Maintain full control over your projects and resources."
                />
            </template>
        </section-text-with-flat-cards>

        <section-text-with-flat-cards class="mt-32 lg:mt-56"
                                      id="saas-hosting"
                                      text-right
                                      title="Hosting for SaaS applications"
        >
            <template #default>
                <p class="text-lg mt-10">
                    Software as a Service (SaaS) is a rapidly growing market, with more and more companies
                    relying on cloud-based applications. Our managed cloud provides the ideal platform
                    for your SaaS application.
                </p>
                <p class="text-lg mt-2">
                    Our platform is specifically tailored to the needs of SaaS applications. It provides
                    the performance, security, and flexibility required to ensure the successful operation
                    of your application.
                </p>
            </template>
            <template #extra>
                <flat-card title="Highest performance"
                           text="Our platform offers you the performance you need for your SaaS application."
                />
                <flat-card title="Security first and foremost"
                           text="Protect your application and your data with our security features."
                />
                <flat-card title="Flexible and scalable"
                           text="Adjust the performance of your application at any time - up or down."
                />
                <flat-card title="Full control"
                           text="Maintain full control over your application and your data."
                />
            </template>
        </section-text-with-flat-cards>
    </div>

</template>
